/**
 * Staff-management state — the same optimistic-apply-then-reconcile shape as
 * `locations/hooks.ts`'s `useManagedLocationsList`, with the four mutations this screen needs
 * (create, change role, reset password, remove) sharing one `mutation` status.
 *
 * One instance for the lifetime of the staff screen, so there is no per-key generation counter to
 * discard a stale request for a previous key — a `mounted` ref is enough, because every mutation
 * awaits the network and the admin can navigate away before it resolves. After any successful
 * mutation the list reconciles against a fresh read in the background, which is also how a
 * server-side change the client did not predict — sessions revoked on a role change, the actor's own
 * row after some other edit — becomes visible without a reload.
 */
import type {
  CreateStaffRequest,
  DeleteResult,
  ResetStaffPasswordRequest,
  StaffList,
  StaffMember,
  UpdateStaffRoleRequest,
} from '@apuestapr/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';
import { accessFor, type MutationState, type RequestState } from '../api/request-state.ts';
import { createStaffApi } from './api.ts';

/** The union of everything a mutation on this screen can hand back, for the shared `mutation` state. */
type StaffMutationResult = StaffMember | DeleteResult;

export interface ManagedStaffList {
  readonly state: RequestState<StaffList>;
  readonly mutation: MutationState<StaffMutationResult>;
  readonly reconciling: boolean;
  readonly create: (input: CreateStaffRequest) => Promise<ApiResult<StaffMember>>;
  readonly updateRole: (
    id: number,
    input: UpdateStaffRoleRequest,
  ) => Promise<ApiResult<StaffMember>>;
  readonly resetPassword: (
    id: number,
    input: ResetStaffPasswordRequest,
  ) => Promise<ApiResult<StaffMember>>;
  readonly remove: (id: number) => Promise<ApiResult<DeleteResult>>;
  readonly reconcile: () => Promise<void>;
}

export const useManagedStaffList = (
  client: ApiClient,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedStaffList => {
  const api = useMemo(() => createStaffApi(client), [client]);
  const [state, setState] = useState<RequestState<StaffList>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<StaffMutationResult>>({ status: 'ready' });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    setReconciling(true);
    const result = await api.list();
    if (!mounted.current) return;
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [api, onFailure]);

  useEffect(() => {
    void api.list().then((result) => {
      if (!mounted.current) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
  }, [api, onFailure]);

  /**
   * The shared tail of every mutation: on failure, record it and surface it; on success, apply the
   * caller's patch to what is on screen, mark ready, and reconcile in the background. The patch is a
   * function rather than a value so a create can append, a role change can replace one row, and a
   * delete can filter — each with the list it actually has.
   */
  const settle = useCallback(
    <Value extends StaffMutationResult>(
      result: ApiResult<Value>,
      applied: (current: StaffList) => StaffList,
    ): ApiResult<Value> => {
      if (!mounted.current) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }
      setState((current) =>
        current.status === 'ready' ? { status: 'ready', data: applied(current.data) } : current,
      );
      setMutation({ status: 'ready', data: result.data });
      void reconcile();
      return result;
    },
    [onFailure, reconcile],
  );

  const create = useCallback(
    async (input: CreateStaffRequest): Promise<ApiResult<StaffMember>> => {
      setMutation({ status: 'loading' });
      const result = await api.create(input, csrfToken);
      return settle(result, (current) => (result.ok ? [...current, result.data] : current));
    },
    [api, csrfToken, settle],
  );

  const updateRole = useCallback(
    async (id: number, input: UpdateStaffRoleRequest): Promise<ApiResult<StaffMember>> => {
      setMutation({ status: 'loading' });
      const result = await api.updateRole(id, input, csrfToken);
      return settle(result, (current) =>
        result.ok ? current.map((row) => (row.id === id ? result.data : row)) : current,
      );
    },
    [api, csrfToken, settle],
  );

  const resetPassword = useCallback(
    async (id: number, input: ResetStaffPasswordRequest): Promise<ApiResult<StaffMember>> => {
      setMutation({ status: 'loading' });
      const result = await api.resetPassword(id, input, csrfToken);
      // A reset changes no visible field, so the patch is identity; it still reconciles.
      return settle(result, (current) => current);
    },
    [api, csrfToken, settle],
  );

  const remove = useCallback(
    async (id: number): Promise<ApiResult<DeleteResult>> => {
      setMutation({ status: 'loading' });
      const result = await api.remove(id, csrfToken);
      return settle(result, (current) => current.filter((row) => row.id !== id));
    },
    [api, csrfToken, settle],
  );

  return { state, mutation, reconciling, create, updateRole, resetPassword, remove, reconcile };
};
